import { defineStore } from 'pinia'
import { useAuthStore } from './auth'

export interface Permission {
  id: number
  name: string
  resource_type: string
  action: string
  description: string
}

export const usePermissionStore = defineStore('permissions', {
  state: () => ({
    permissions: [] as Permission[],
    isLoading: false,
    isInitialized: false,
    error: null as string | null,
  }),

  getters: {
    /**
     * Check if the user has a specific permission
     */
    hasPermission: (state) => (resourceType: string, action: string): boolean => {
      const authStore = useAuthStore()
      
      // If user is not authenticated, they have no permissions
      if (!authStore.isAuthenticated) {
        return false
      }
      
      // Check if the user has the specific permission
      return state.permissions.some(
        p => p.resource_type === resourceType && p.action === action
      )
    },
    
    /**
     * Get all permissions for a specific resource type
     */
    getPermissionsByResourceType: (state) => (resourceType: string): Permission[] => {
      return state.permissions.filter(p => p.resource_type === resourceType)
    },
    
    /**
     * Check if the user has any permission for a specific resource type
     */
    hasAnyPermissionForResource: (state) => (resourceType: string): boolean => {
      const authStore = useAuthStore()
      
      // If user is not authenticated, they have no permissions
      if (!authStore.isAuthenticated) {
        return false
      }
      
      return state.permissions.some(p => p.resource_type === resourceType)
    }
  },

  actions: {
    /**
     * Fetch the current user's permissions from the API
     */
    async fetchPermissions(): Promise<void> {
      const authStore = useAuthStore()
      
      // If user is not authenticated, don't fetch permissions
      if (!authStore.isAuthenticated) {
        this.permissions = []
        this.isInitialized = true
        return
      }
      
      try {
        this.isLoading = true
        this.error = null
        
        // Fetch permissions from the API
        const permissions = await $fetch<Permission[]>('/api/v1/permissions/users/me/permissions', {
          headers: {
            Authorization: `Bearer ${authStore.token}`
          }
        })
        
        this.permissions = permissions
        this.isInitialized = true
      } catch (error: any) {
        console.error('Failed to fetch permissions:', error)
        this.error = error.message || 'Failed to fetch permissions'
        this.permissions = []
      } finally {
        this.isLoading = false
      }
    },
    
    /**
     * Clear all permissions (used during logout)
     */
    clearPermissions(): void {
      this.permissions = []
      this.isInitialized = false
      this.error = null
    },
    
    /**
     * Initialize permissions - should be called after authentication
     */
    async initPermissions(): Promise<void> {
      if (!this.isInitialized) {
        await this.fetchPermissions()
      }
    }
  }
})
